import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { challengeApi, type TrackInfo } from '../utils/api'
import AuthHeader from '../components/AuthHeader'
import Logo from '../components/Logo'

const TRACK_COLORS: Record<string, { bg: string; border: string; text: string; gradient: string }> = {
  beginner: {
    bg: 'bg-green-50',
    border: 'border-green-200 hover:border-green-400',
    text: 'text-green-700',
    gradient: 'from-green-500 to-emerald-500',
  },
  intermediate: {
    bg: 'bg-blue-50',
    border: 'border-blue-200 hover:border-blue-400',
    text: 'text-blue-700',
    gradient: 'from-blue-500 to-indigo-500',
  },
  advanced: {
    bg: 'bg-purple-50',
    border: 'border-purple-200 hover:border-purple-400',
    text: 'text-purple-700',
    gradient: 'from-purple-500 to-pink-500',
  },
}

const TRACK_EMOJIS: Record<string, string> = {
  beginner: '\u26A1',
  intermediate: '\uD83D\uDD2C',
  advanced: '\uD83E\uDDEC',
}

function ArenaPage() {
  const [tracks, setTracks] = useState<TrackInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    challengeApi.getTracks()
      .then(r => setTracks(r.tracks))
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-qcloud-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <Logo size="small" />
              <span className="text-xl font-bold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent">
                QuantumArena
              </span>
            </Link>
            <span className="text-qcloud-muted">/</span>
            <span className="font-semibold text-qcloud-text">Challenge Arena</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">Home</Link>
            <Link to="/hardware" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">Hardware</Link>
            <AuthHeader />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-12 px-4">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-qcloud-text mb-4">
            Challenge Arena
          </h1>
          <p className="text-lg text-qcloud-muted max-w-2xl mx-auto">
            Master quantum computing through three progressive tracks.
            Write circuits, run them on real quantum hardware, and compete on the leaderboard.
          </p>
        </div>

        {/* Track Cards */}
        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-qcloud-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {tracks.map(track => {
              const colors = TRACK_COLORS[track.id] || TRACK_COLORS.beginner
              const emoji = TRACK_EMOJIS[track.id] || '\u2728'

              return (
                <Link
                  key={track.id}
                  to={`/arena/${track.id}`}
                  className={`${colors.bg} rounded-2xl border-2 ${colors.border} p-8 hover:shadow-xl transition-all group relative overflow-hidden`}
                >
                  {/* Background decoration */}
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${colors.gradient} opacity-5 rounded-full -translate-y-1/2 translate-x-1/2`} />

                  <div className="relative">
                    <div className="text-4xl mb-4">{emoji}</div>
                    <h2 className={`text-2xl font-bold ${colors.text} mb-2`}>
                      {track.title}
                    </h2>
                    <p className="text-sm text-qcloud-muted mb-6 min-h-[60px]">
                      {track.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-qcloud-muted">
                        <span className="font-semibold text-qcloud-text">{track.challenge_count}</span> challenges
                      </div>
                      <div className={`px-3 py-1.5 rounded-lg bg-white/80 ${colors.text} text-sm font-medium group-hover:bg-white transition-colors`}>
                        Enter &rarr;
                      </div>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}

        {/* Info Section */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl border border-qcloud-border p-6">
            <h3 className="font-semibold text-qcloud-text mb-2">Multi-Language Support</h3>
            <p className="text-sm text-qcloud-muted">
              Write solutions in Qiskit, Stim, or Qrisp. Each challenge specifies which languages are supported.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-qcloud-border p-6">
            <h3 className="font-semibold text-qcloud-text mb-2">Real Hardware</h3>
            <p className="text-sm text-qcloud-muted">
              Test on simulators first, then submit to real IBM quantum processors. Separate leaderboards for each.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-qcloud-border p-6">
            <h3 className="font-semibold text-qcloud-text mb-2">Progressive Learning</h3>
            <p className="text-sm text-qcloud-muted">
              Start with basic gates, advance to algorithms, and tackle fault-tolerant quantum computing challenges.
            </p>
          </div>
        </section>
      </main>
    </div>
  )
}

export default ArenaPage
